import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { Location } from "@/pages/configurations/locations/models/Location/Location";
import {
	deleteLocation,
	getLocationById,
} from "@/pages/configurations/locations/repositories/Location";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	locationIdForDelete: number | null;
};

export const DeleteLocation = ({
	show,
	onHide,
	locationIdForDelete,
}: Props) => {
	const classes = useStyles();
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);

	const [locationForDelete, setLocationForDelete] = useState<Location | null>(
		null,
	);

	const [error, setError] = useState<string | null>(null);

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const handleDelete = async () => {
		setIsSubmitLoading(true);
		try {
			const res = await deleteLocation(locationIdForDelete);
			if (res.ok) {
				setMessage("The location was deleted successfully");
				setMessageType("success");
				onHide(true);
				setLocationForDelete(null);
			}
		} catch (e) {
			setMessage("Error, please try again");
			setMessageType("error");

			setError(e as string);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const { location } = await getLocationById(locationIdForDelete);
				if (location?.success) {
					setLocationForDelete(location.result);
				} else {
					onHide(true);
					setMessage("Error, location not found. Try again");
					setOpenAlertMessage(true);
					setLocationForDelete(null);
				}

				setIsLoading(false);
			})();
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="deleteLocation"
					title={"Delete a location"}
					maxWidth="sm"
				>
					<Modal.Content
						className={classes.modalBody}
						style={{ padding: "40px 30px" }}
					>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "80px" }}
							>
								<CircularProgress size="3rem" />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Typography>
										You will not be able to recover deleted data
									</Typography>
								</Grid>
								<br />
								<Grid container spacing={1}>
									<Typography>
										Are you sure you want to delete{" "}
										<strong>{locationForDelete?.locationName}</strong>?
									</Typography>
								</Grid>
							</>
						)}
					</Modal.Content>
					<Modal.Actions style={{ padding: "1rem" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => {
										onHide(false);
										setLocationForDelete(null);
									}}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="secondary"
									onClick={handleDelete}
								>
									Delete
								</Button>
							</Grid>
						</Grid>
					</Modal.Actions>
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
